import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import type { QixitAuthApi } from './types';

type Props = {
  api: QixitAuthApi;
  open: boolean;
  onClose: () => void;
  initialUsername?: string;
};

export default function ForgotPasswordDialog({ api, open, onClose, initialUsername }: Props) {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUsername(initialUsername && !initialUsername.includes('@') ? initialUsername.trim() : '');
    setSuccess(false);
    setError(null);
  }, [open, initialUsername]);

  const handleSubmit = async () => {
    if (!username.trim()) {
      setError('Please enter your username');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('/api/Auth/forgot-password-alias', { username: username.trim() });
      setSuccess(true);
    } catch {
      setError('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forgot Password</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            If an account exists with that username, a password reset link has been sent to the
            associated email address. Please check your inbox and spam folder.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
              Enter your username and we'll send a reset link to its registered email address.
              If you don't remember your username, use <strong>Forgot Username</strong> first.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit();
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{success ? 'Close' : 'Cancel'}</Button>
        {!success && (
          <Button onClick={() => void handleSubmit()} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
